import { createAsyncThunk } from '@reduxjs/toolkit';
import { store } from 'app/store';
import { MAX_ENTITIES_NUMBER_PER_REQUEST, NUMBER_OF_RETRIES } from 'common/constants/exportData';
import { Acl, FormattedAclWithLicenseInfo, License } from 'common/types/Entities';
import { getNumberOfRequests, mergeResults } from 'common/utils/exportDataApiHelper';
import { ConflictErrorOrigin } from 'common/utils/LicenseAclEntriesErrorHelpers';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';

import { APIBaseResponse } from '..';
import { handleListFetch } from '../apiHelpers';
import { APILicensesGet } from '../Licenses/Licenses.api';
import { _formatLicenseResponse } from '../Licenses/LicenseThunkHelpers';
import { APITenantUserMembershipGetSingle } from '../TenantUserMembership/TenantUserMembership.api';
import { TenantUserMembershipGetSingleResponse } from '../TenantUserMembership/TenantUserMembershipApiTypeDefs';
import { APIUserGroupPost } from '../UserGroups/UserGroups.api';

import {
    AclSearchPaginationInfo,
    APIAclEntriesListParams,
    DeleteAclEntryThunkParams,
    GetAclWithProfileEntriesParams,
    PostAclEntryThunkParams,
    PutAclEntryThunkParams
} from './AclEntitiesApiTypeDefs';
import {
    _formatAcl,
    _formatAclWithProfile,
    _formatUserAclsWithLicenseInfo,
    _formatUserGroupAclsWithLicenseInfo,
    _getAclEntriesDeleteParams,
    _getAclEntriesPostPutParams,
    _isLicenseAclEntriesPage,
} from './AclEntitiesThunkHelpers';
import {
    APIAclEntriesGetList,
    APIAclEntryDelete,
    APIAclEntryPost,
    APIAclEntryPut,
    APIAclWithProfileEntriesGet,
} from './AclEntries.api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createThunkErrorResponse = (thunkAPI: any, error: any, errorOrigin: ConflictErrorOrigin) => {
    return thunkAPI.rejectWithValue({
        message: error.message || `Failed to create ${errorOrigin}`,
        status: error.status || error.response?.status,
        errorOrigin,
        originalError: error
    });
};

export const fetchAclEntries = createAsyncThunk('aclEntries/fetchAclEntries', async (params: APIAclEntriesListParams) => {
    const { tenantId, userId, usergroupId, licenseId, isActive, licenseFilterType } = params;
    const response = await APIAclEntriesGetList({ tenantId, userId, usergroupId, licenseId, isActive, licenseFilterType });
    return _formatAcl(response.data);
});

export const fetchAclWithProfileEntries = createAsyncThunk('aclEntries/fetchAclWithProfileEntries', async (params: GetAclWithProfileEntriesParams) => {
    const response = await APIAclWithProfileEntriesGet(params);
    return _formatAclWithProfile(response);
});

export const fetchAclWithProfileEntriesForExport = createAsyncThunk('aclEntries/fetchAclWithProfileEntriesForExport', async (params: GetAclWithProfileEntriesParams) => {
    const numberOfRequests = getNumberOfRequests(params.rowsCount);
    const fetchPromises = Array.from({ length: numberOfRequests }, async (_, i) => APIAclWithProfileEntriesGet({
        ...params,
        skip: i * MAX_ENTITIES_NUMBER_PER_REQUEST,
        take: MAX_ENTITIES_NUMBER_PER_REQUEST,
        retries: NUMBER_OF_RETRIES
    }));

    const response = (await Promise.all(fetchPromises))
        .map((result)=>{
            return {
                ..._formatAclWithProfile(result)
            };
        });

    return mergeResults(response);
});

export const postAclEntry = createAsyncThunk('aclEntries/postAclEntry', async (params: PostAclEntryThunkParams, thunkAPI) => {
    const item = params.item;
    const { tenantRequiresLicense } = item;

    if (item.shouldCreateNewUserGroup && !!item.newUserGroupName) {
        try {
            const createUserGroupResponse = await APIUserGroupPost({
                tenantId: item.tenantId,
                name: item.newUserGroupName,
                description: item.newUserGroupDescription || ''
            });
            item.usergroupId = createUserGroupResponse.data.userGroupId;
        } catch (error) {
            return createThunkErrorResponse(thunkAPI, error, ConflictErrorOrigin.USER_GROUP_CREATION);
        }
    }

    const postParams = _getAclEntriesPostPutParams(item);
    let response: APIBaseResponse;
    try {
        response = await APIAclEntryPost(postParams);
    } catch (error) {
        return createThunkErrorResponse(thunkAPI, error, ConflictErrorOrigin.ACL_ENTRY_CREATION);
    }

    const baseThunkResponseHandler = tenantRequiresLicense
        ? handleBaseThunkResponseWithLicenseInfo
        : handleBaseThunkResponse;

    if (_isLicenseAclEntriesPage(params.aclPage)) {
        const paginatedResponse = await handlePaginatedThunkResponse(item, params.paginationInfo);
        const { data, skipped, taken, count } = paginatedResponse;
        return {
            item,
            response,
            rows: data,
            skipped: skipped,
            taken: taken,
            count
        };
    } else {
        const rows = await baseThunkResponseHandler(params.aclPage, item);
        return {
            item,
            response,
            rows
        };
    }
});

export const putAclEntry = createAsyncThunk('aclEntries/putAclEntry', async (params: PutAclEntryThunkParams) => {
    const item = params.item;
    const { tenantRequiresLicense } = item;
    const putParams = _getAclEntriesPostPutParams(item);

    const response = await APIAclEntryPut(putParams);

    const baseThunkResponseHandler = tenantRequiresLicense
        ? handleBaseThunkResponseWithLicenseInfo
        : handleBaseThunkResponse;

    if(_isLicenseAclEntriesPage(params.aclPage)) {
        const paginatedResponse = await handlePaginatedThunkResponse(item, params.paginationInfo);
        const { data, skipped, taken, count } = paginatedResponse;
        return {
            item,
            response,
            rows: data,
            skipped: skipped,
            taken: taken,
            count
        };
    } else {
        const rows = await baseThunkResponseHandler(params.aclPage, item);
        return {
            item,
            response,
            rows
        };
    }
});

export const deleteAclEntry = createAsyncThunk('aclEntries/deleteAclEntries', async (params: DeleteAclEntryThunkParams) => {
    const item = params.item;
    const { tenantRequiresLicense } = item;
    const deleteParams = _getAclEntriesDeleteParams(item);

    const response = await APIAclEntryDelete(deleteParams);

    const baseThunkResponseHandler = tenantRequiresLicense
        ? handleBaseThunkResponseWithLicenseInfo
        : handleBaseThunkResponse;

    if(_isLicenseAclEntriesPage(params.aclPage)) {
        const paginatedResponse = await handlePaginatedThunkResponse(item, params.paginationInfo);
        const { data, skipped, taken, count } = paginatedResponse;
        return {
            item,
            response,
            rows: data,
            skipped: skipped,
            taken: taken,
            count
        };
    } else {
        const rows = await baseThunkResponseHandler(params.aclPage, item);
        return {
            item,
            response,
            rows
        };
    }
});

export const fetchUserAclEntriesDetails = createAsyncThunk(
    'tenants/fetchUserAclEntriesDetails',
    async (item: { tenantId: string, userId: string }): Promise<FormattedAclWithLicenseInfo[]> => {
        const { tenantId, userId } = item;
        const requests = [
            APIAclEntriesGetList({ tenantId, userId }),
            APILicensesGet({ tenantId }),
            APITenantUserMembershipGetSingle({ userId, tenantId })
        ];

        const responses = await Promise.all(requests);
        const userAclEntries = _formatAcl(responses[0].data as Acl[]);
        const licenses = _formatLicenseResponse(responses[1].data as License[]);
        const userTenantMembership = responses[2].data as TenantUserMembershipGetSingleResponse;

        const aclsWithLicenseInfo = _formatUserAclsWithLicenseInfo({
            userAclEntries,
            licenses,
            userTenantMembership
        });

        return aclsWithLicenseInfo;
    }
);

export const fetchUserGroupAclEntriesDetails = createAsyncThunk(
    'tenants/fetchUserGroupAclEntriesDetails',
    async (item: { tenantId: string, usergroupId?: string, userId?: string }): Promise<FormattedAclWithLicenseInfo[]> => {
        const { tenantId, usergroupId, userId } = item;
        const requests = [
            APIAclEntriesGetList({ tenantId, userId, usergroupId }),
            APILicensesGet({ tenantId })
        ];

        const responses = await Promise.all(requests);
        const userGroupAclEntries = _formatAcl(responses[0].data as Acl[]);
        const licenses = _formatLicenseResponse(responses[1].data as License[]);

        const usergroupAclsWithLicenseInfo = _formatUserGroupAclsWithLicenseInfo({
            userGroupAclEntries,
            licenses
        });

        return usergroupAclsWithLicenseInfo;
    }
);

async function handleBaseThunkResponse(
    aclPage: AclEntriesPage,
    item: {
        tenantId: string;
        licenseId?: string;
        userId?: string;
        usergroupId?: string;
        tenantRequiresLicense?: boolean;
    })
{
    if (aclPage === AclEntriesPage.UserProfile) {
        return [];
    }

    const { tenantId, userId, usergroupId } = item;

    const getListParams: APIAclEntriesListParams = {
        tenantId: tenantId,
        userId: aclPage === AclEntriesPage.License ? undefined : userId,
        usergroupId: aclPage === AclEntriesPage.License ? undefined : usergroupId
    };

    const getListPromise = APIAclEntriesGetList(getListParams);
    const rows = _formatAcl(await handleListFetch(getListPromise));

    return rows;
}

async function handlePaginatedThunkResponse(
    item: {
        tenantId: string;
        licenseId?: string;
        userId?: string;
        usergroupId?: string;

    }, paginationInfo?: AclSearchPaginationInfo)
{
    const paginatedListParams: GetAclWithProfileEntriesParams = {
        licenseId: item.licenseId ?? '',
        tenantId: item.tenantId,
        search: paginationInfo?.search,
        skip: paginationInfo?.skip,
        take: paginationInfo?.take,
        sortKey: paginationInfo?.sortKey,
        sortOrder: paginationInfo?.sortOrder
    };
    const aclWithProfilesResponse = await APIAclWithProfileEntriesGet(paginatedListParams);
    return _formatAclWithProfile(aclWithProfilesResponse);
}

async function handleBaseThunkResponseWithLicenseInfo(
    aclPage: AclEntriesPage,
    item: {
        tenantId: string;
        licenseId?: string;
        userId?: string;
        usergroupId?: string;
        tenantRequiresLicense?: boolean;
    })
{
    const { dispatch } = store;
    const { tenantId, userId, usergroupId } = item;

    try {
        switch(aclPage) {
            case AclEntriesPage.TenantUser: {
                const userAclEntriesWithLicenseInfo = await dispatch(
                    fetchUserAclEntriesDetails({
                        tenantId,
                        userId: userId ?? ''
                    })
                )
                    .unwrap();
                return userAclEntriesWithLicenseInfo;
            }
            case AclEntriesPage.TenantUserGroup: {
                const userAclEntriesWithLicenseInfo = await dispatch(
                    fetchUserGroupAclEntriesDetails({
                        tenantId,
                        usergroupId,
                        userId: userId ?? ''
                    })
                )
                    .unwrap();
                return userAclEntriesWithLicenseInfo;
            }
            default: {
                return [] as FormattedAclWithLicenseInfo[];
            }

        }
    } catch (error) {
        console.error(error);
        return [] as FormattedAclWithLicenseInfo[];
    }
}
